/**
 * Projects edit form component.
 *
 * @category forms
 */

import { MouseEvent, ReactElement, useState, useEffect, useMemo } from "react";
import { AxiosProgressEvent } from "axios";
import { useLocation } from "_/components/router";
import { concat, remove } from "ramda";
import { useForm, Controller } from "react-hook-form";
import { t } from "@lingui/core/macro";

import { useCurrentOrg } from "_/data/orgs";
import {
  useCreateProject,
  useDeleteProject,
  useUpdateProject,
  useReviseProject,
  useRevisionResources,
  Project,
  Revision,
  RevisionChange,
} from "_/data/projects";
import { BlobRecord } from "_/data/blob";

import { FormTextField } from "_/components/text-field";
import { FormField } from "_/components/form-field";
import { OrgUserSelect } from "_/components/user-select";
import { DropZone, FileRejection } from "_/components/dropzone";
import { MaterialSelect } from "_/components/materials";
import { Icon } from "_/components/icon";
import { ProgressBar } from "_/components/progress-bar";
import { useAddToast } from "_/components/toasts";
import { SubmitCancelButtons } from "_/components/modal";
import { Form } from "_/components/form";

import { projectUrls } from "_/routes";
import { formatBytes } from "_/utils";

import * as S from "./styled";

const GCODE_EXTENSIONS = ["gcode", "g", "gc", "nc"];

const GCODE_ACCEPT = {
  "text/plain": GCODE_EXTENSIONS.map((ext) => `.${ext}`),
  "text/x.gcode": GCODE_EXTENSIONS.map((ext) => `.${ext}`),
};

const STL_EXTENSIONS = ["stl"];
const THREEMF_EXTENSIONS = ["3mf"];

// This dropzone accepts both STL and 3MF model files.
const MODEL_ACCEPT = {
  "application/sla": STL_EXTENSIONS.map((ext) => `.${ext}`),
  "model/3mf": THREEMF_EXTENSIONS.map((ext) => `.${ext}`),
};

function bytesPretty(file: File | BlobRecord): string {
  const bytes = file instanceof File ? file.size : file.uncompressedSize;
  return formatBytes(bytes);
}

// Determine the type of the file.
function fileType(
  file: File | BlobRecord
): "gcode" | "stl" | "3mf" | undefined {
  // If file is a File, use the extension to determine its type.
  if (file instanceof File) {
    // `split` returns a non-empty array, so `pop` result is never undefined.
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const extension = (file.name.split(".").pop() as string).toLowerCase();

    if (extension) {
      if (GCODE_EXTENSIONS.includes(extension)) {
        return "gcode";
      }
      if (STL_EXTENSIONS.includes(extension)) {
        return "stl";
      }
    }
    if (THREEMF_EXTENSIONS.includes(extension)) {
      return "3mf";
    }
    console.warn("Unexpected file type for file:", file.name);
    return undefined;
  } else {
    switch (file.kind) {
      case "stl":
      case "gcode":
      case "3mf":
        return file.kind;
      // Adding a default case here to satisfy TS that all cases are handled.
      // `kind` property should always be present.
      // oxlint-disable-next-line unicorn/no-useless-switch-case
      case "unknown":
      default:
        console.warn("Unexpected file type for blob:", file.name);
        return undefined;
    }
  }
}

type RevisionSettings = {
  gcodeFile: BlobRecord | File | undefined;
  modelFiles: (File | BlobRecord)[];
  material1: string | null;
  material2: string | null;
};

const FileItem = ({
  file,
  onClick,
  onRemove,
}: {
  file: File | BlobRecord;
  onClick?: (e: MouseEvent) => void;
  onRemove?: (e: MouseEvent) => void;
}) => (
  <S.FileItem onClick={onClick}>
    <S.FileName>{file.name}</S.FileName>
    <S.FileSize>{bytesPretty(file)}</S.FileSize>
    {onRemove && <Icon variant="Delete" onClick={onRemove} />}
  </S.FileItem>
);

/**
 * Helper component for project revision form to be rendered within modal.
 */
export const ProjectEditForm = ({
  project,
  onComplete,
  isUploading,
  setIsUploading,
}: {
  project: Project;
  onComplete: () => void;
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
}): ReactElement => {
  const updateProject = useUpdateProject();
  const reviseProject = useReviseProject();

  const [_location, setLocation] = useLocation();

  const addToast = useAddToast();

  const revision = project.revisions[project.revisions.length - 1];

  const { data: files = [] } = useRevisionResources(revision);

  // Get the initial values for form fields that will create a new revision if changed
  const currentRevisionSettings = useMemo<RevisionSettings>(() => {
    return {
      gcodeFile: revision.gcodeFile?.file,
      modelFiles: files.filter((f) => f.kind === "stl" || f.kind === "3mf"),
      material1: revision.materials[0],
      material2: revision.materials[1],
    };
  }, [files, revision.materials, revision.gcodeFile?.file]);

  const [gcodeFile, setGcodeFile] = useState(currentRevisionSettings.gcodeFile);
  const [modelFiles, setModelFiles] = useState(
    currentRevisionSettings.modelFiles
  );

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues: { ...project, ...currentRevisionSettings },
  });

  // Watch for changes to the material form fields
  const updatedMaterial1 = watch("material1");
  const updatedMaterial2 = watch("material2");

  const [willCreateRevision, setWillCreateRevision] = useState(false);

  const [uploadProgress, setUploadProgress] = useState<number>();

  // Update form value and validate whenever gcodeFile changes
  useEffect(() => {
    setValue("gcodeFile", gcodeFile);
  }, [gcodeFile, setValue]);

  // Update form value and validate whenever gcodeFile changes
  useEffect(() => {
    setValue("modelFiles", modelFiles);
  }, [modelFiles, setValue]);

  // Check for changes on form fields that will trigger the creation of
  // a new revision
  useEffect(() => {
    const materialsChanged =
      updatedMaterial1 !== currentRevisionSettings.material1 ||
      updatedMaterial2 !== currentRevisionSettings.material2;

    const gcodeFileChanged = currentRevisionSettings.gcodeFile !== gcodeFile;

    const modelFilesChanged =
      currentRevisionSettings.modelFiles.length !== modelFiles.length ||
      !currentRevisionSettings.modelFiles.every(
        (file, index) => file === modelFiles[index]
      );

    if (materialsChanged || gcodeFileChanged || modelFilesChanged) {
      setWillCreateRevision(true);
    } else {
      setWillCreateRevision(false);
    }
  }, [
    gcodeFile,
    modelFiles,
    updatedMaterial1,
    updatedMaterial2,
    currentRevisionSettings,
  ]);

  async function submit(data: Project & RevisionSettings) {
    try {
      // First update the project-level changes (project name and owner)
      await updateProject.mutateAsync(data);

      // If associated files or material choices have been updated, also
      // create a new revision for the project.
      if (willCreateRevision) {
        const revisionChange: RevisionChange = {
          gcode: data.gcodeFile,
          models: data.modelFiles,
          materials: {
            material1Id: data.material1,
            material2Id: data.material2,
          },
        };

        // Any of the files associated with this revision change are not
        // `BlobRecord`s, we will need to upload.
        const willUpload =
          data.gcodeFile instanceof File ||
          data.modelFiles.some((file) => file instanceof File);

        const onUploadProgress = willUpload
          ? (progressEvent: AxiosProgressEvent) =>
              setUploadProgress(progressEvent.progress)
          : undefined;

        if (willUpload) {
          setIsUploading(true);
        }

        await reviseProject.mutateAsync({
          projectId: project.id,
          revisionChange,
          onUploadProgress,
          onSuccess: (newRevision: Revision) => {
            addToast({
              title: t`components.project-edit.revision-number-created ${newRevision.number}`,
              kind: "success",
              timeout: 5_000,
            });

            setLocation(projectUrls.prepare(project.id, newRevision.id));
          },
        });

        setIsUploading(false);
      }
    } catch (e) {
      console.error("Error while updating project.", e);

      addToast({
        title: t`components.project-edit.revision-creation-failed`,
        kind: "warning",
        timeout: 10_000,
      });
    } finally {
      // Call `onComplete` to allow any cleanup to be done by the calling
      // component like closing modals, nav, etc.
      onComplete();
    }
  }

  function handleGcodeDrop(newFiles: File[], rejectedFiles: FileRejection[]) {
    // Take the first G-code file and set it as the G-code file.
    const newFile = newFiles[0];

    if (newFile) {
      setGcodeFile(newFiles[0]);
    }

    // If any STL or 3MF files were dropped into the G-code dropzone, add them
    // to the list of model files.
    const newModelFiles = rejectedFiles
      .filter((f) => fileType(f.file) === "stl" || fileType(f.file) === "3mf")
      .map((f) => f.file);

    if (newModelFiles.length > 0) {
      setModelFiles((fs) => concat(fs, newModelFiles));
    }
  }

  function handleModelDrop(newFiles: File[], rejectedFiles: FileRejection[]) {
    if (newFiles.length > 0) {
      setModelFiles((fs) => concat(fs, newFiles));
    }

    // If any G-code files were dropped into the STL dropzone, set the G-code
    // file to the first one.
    const newGcodeFile = rejectedFiles.find(
      (f) => fileType(f.file) === "gcode"
    )?.file;

    if (newGcodeFile) {
      setGcodeFile(newGcodeFile);
    }
  }

  function removeModelFile(i: number) {
    setModelFiles((fs) => remove(i, 1, fs));
  }

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        rules={{ required: t`components.projects-view.name-required` }}
        placeholder={t`components.projects-view.project-name`}
      />
      <Controller
        control={control}
        name="ownerId"
        render={({ field, fieldState }) => (
          <OrgUserSelect
            {...field}
            label={t`common.owner`}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
            required
          />
        )}
      />
      <Controller
        control={control}
        name="material1"
        rules={{ required: t`components.projects-view.material-required` }}
        render={({ field, fieldState }) => (
          <MaterialSelect
            {...field}
            label={t`common.primary-material`}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
            required
          />
        )}
      />
      <Controller
        control={control}
        name="material2"
        render={({ field, fieldState }) => (
          <MaterialSelect
            {...field}
            label={t`common.secondary-material`}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
            isClearable
          />
        )}
      />
      <Controller
        control={control}
        name="gcodeFile"
        rules={{ required: t`components.projects-edit.gcode-required` }}
        render={({ fieldState }) => (
          <FormField
            label={t`common.gcode`}
            hint={fieldState.error?.message}
            invalid={fieldState.invalid}
          >
            <DropZone
              onDrop={handleGcodeDrop}
              text={t`components.project-edit.gcode-dropzone-text`}
              dropText={t`components.project-edit.dropzone-droptext`}
              chipText={t`components.project-edit.dropzone-chiptext`}
              accept={GCODE_ACCEPT}
              invalid={fieldState.invalid}
            >
              {gcodeFile && (
                <FileItem
                  file={gcodeFile}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              )}
            </DropZone>
          </FormField>
        )}
      />
      <Controller
        control={control}
        name="modelFiles"
        render={({ fieldState }) => (
          <FormField
            label={t`components.project-edit.stl`}
            hint={fieldState.error?.message}
            invalid={fieldState.invalid}
          >
            <DropZone
              onDrop={handleModelDrop}
              text={t`components.project-edit.stl-dropzone-text`}
              dropText={t`components.project-edit.dropzone-droptext`}
              chipText={t`components.project-edit.dropzone-chiptext`}
              accept={MODEL_ACCEPT}
            >
              {modelFiles.map((f: File | BlobRecord, i: number) => (
                <FileItem
                  file={f}
                  key={`${i}_${f.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onRemove={(e) => {
                    e.stopPropagation();
                    removeModelFile(i);
                  }}
                />
              ))}
            </DropZone>
          </FormField>
        )}
      />
      {uploadProgress && (
        <ProgressBar
          value={uploadProgress}
          title={t`components.project-edit.uploading-files`}
        />
      )}
      <SubmitCancelButtons
        onCancel={onComplete}
        disableSubmit={isUploading}
        disableCancel={isUploading}
      />
    </Form>
  );
};

/**
 * Helper component for project creation form to be rendered within modal.
 */
export const ProjectCreationForm = ({
  onComplete,
  isUploading,
  setIsUploading,
}: {
  onComplete: () => void;
  isUploading: boolean;
  setIsUploading: (isUploading: boolean) => void;
}): ReactElement => {
  const createProject = useCreateProject();
  const deleteProject = useDeleteProject();
  const reviseProject = useReviseProject();
  const { data: org } = useCurrentOrg();

  const [_location, setLocation] = useLocation();

  const addToast = useAddToast();

  const [gcodeFile, setGcodeFile] = useState<File | BlobRecord>();
  const [modelFiles, setModelFiles] = useState<(File | BlobRecord)[]>([]);

  const defaultValues: Pick<Project, "name"> & RevisionSettings = {
    name: "",
    gcodeFile: undefined,
    modelFiles: [],
    material1: null,
    material2: null,
  };

  const { control, handleSubmit, setValue, trigger } = useForm({
    defaultValues,
  });

  const [uploadProgress, setUploadProgress] = useState<number>();

  // Update form value and validate whenever gcodeFile changes
  useEffect(() => {
    setValue("gcodeFile", gcodeFile);

    // Don't validate if there is no G-code. Otherwise, the error state
    // will be triggered before the user has interacted with the form.
    if (gcodeFile) {
      void trigger("gcodeFile");
    }
  }, [gcodeFile, setValue, trigger]);

  // Update form value whenever modelFiles changes
  useEffect(() => {
    setValue("modelFiles", modelFiles);
  }, [modelFiles, setValue, trigger]);

  async function submit(data: Pick<Project, "name"> & RevisionSettings) {
    // Create the new project.
    const project = await createProject.mutateAsync({
      name: data.name,
      description: "",
      domainId: org.domainId,
    });

    const revisionChange: RevisionChange = {
      gcode: data.gcodeFile,
      models: data.modelFiles,
      materials: {
        material1Id: data.material1,
        material2Id: data.material2,
      },
    };

    // Try creating the first revision for the new project.
    try {
      setIsUploading(true);

      await reviseProject.mutateAsync({
        projectId: project.id,
        revisionChange,
        onUploadProgress: (progressEvent: AxiosProgressEvent) =>
          setUploadProgress(progressEvent.progress),
        onSuccess: (newRevision: Revision) => {
          setLocation(projectUrls.prepare(project.id, newRevision.id));
        },
      });
    } catch (e) {
      // Remove project if error during initial revision creation.
      console.warn("Error during revision creation:", e);

      addToast({
        title: t`components.project-edit.project-creation-failed`,
        kind: "warning",
        timeout: 10_000,
      });

      await deleteProject.mutateAsync({ id: project.id });

      throw e;
    }

    // Call `onComplete` to allow any cleanup to be done by the calling
    // component like closing modals, nav, etc.
    onComplete();
  }

  function handleGcodeDrop(newFiles: File[], rejectedFiles: FileRejection[]) {
    // Take the first G-code file and set it as the G-code file.
    const newFile = newFiles[0];

    if (newFile) {
      setGcodeFile(newFiles[0]);
    }

    // If any STL or 3MF files were dropped into the G-code dropzone, add them
    // to the list of model files.
    const newModelFiles = rejectedFiles
      .filter((f) => fileType(f.file) === "stl" || fileType(f.file) === "3mf")
      .map((f) => f.file);

    if (newModelFiles.length > 0) {
      setModelFiles((fs) => concat(fs, newModelFiles));
    }
  }

  function handleModelDrop(newFiles: File[], rejectedFiles: FileRejection[]) {
    if (newFiles.length > 0) {
      setModelFiles((fs) => concat(fs, newFiles));
    }

    // If any G-code files were dropped into the STL dropzone, set the G-code
    // file to the first one.
    const newGcodeFile = rejectedFiles.find(
      (f) => fileType(f.file) === "gcode"
    )?.file;

    if (newGcodeFile) {
      setGcodeFile(newGcodeFile);
    }
  }

  function removeModelFile(i: number) {
    setModelFiles((fs) => remove(i, 1, fs));
  }

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <FormTextField
        control={control}
        name="name"
        label={t`common.name`}
        rules={{ required: t`components.projects-view.name-required` }}
        placeholder={t`components.projects-view.project-name`}
      />
      <Controller
        control={control}
        name="material1"
        rules={{ required: t`components.projects-view.material-required` }}
        render={({ field, fieldState }) => (
          <MaterialSelect
            {...field}
            label={t`common.primary-material`}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="material2"
        render={({ field }) => (
          <MaterialSelect
            {...field}
            label={t`common.secondary-material`}
            isClearable
          />
        )}
      />
      <Controller
        control={control}
        name="gcodeFile"
        rules={{ required: t`components.projects-edit.gcode-required` }}
        render={({ fieldState }) => (
          <FormField
            label={t`common.gcode`}
            hint={fieldState.error?.message}
            invalid={fieldState.invalid}
          >
            <DropZone
              onDrop={handleGcodeDrop}
              text={t`components.project-edit.gcode-dropzone-text`}
              dropText={t`components.project-edit.dropzone-droptext`}
              chipText={t`components.project-edit.dropzone-chiptext`}
              accept={GCODE_ACCEPT}
              invalid={fieldState.invalid}
            >
              {gcodeFile && (
                <FileItem
                  file={gcodeFile}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              )}
            </DropZone>
          </FormField>
        )}
      />
      <Controller
        control={control}
        name="modelFiles"
        render={() => (
          <FormField label={t`components.project-edit.stl`}>
            <DropZone
              onDrop={handleModelDrop}
              text={t`components.project-edit.stl-dropzone-text`}
              dropText={t`components.project-edit.dropzone-droptext`}
              chipText={t`components.project-edit.dropzone-chiptext`}
              accept={MODEL_ACCEPT}
            >
              {modelFiles.map((f: File | BlobRecord, i: number) => (
                <FileItem
                  file={f}
                  key={`${i}_${f.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  onRemove={(e) => {
                    e.stopPropagation();
                    removeModelFile(i);
                  }}
                />
              ))}
            </DropZone>
          </FormField>
        )}
      />
      {uploadProgress && (
        <ProgressBar
          value={uploadProgress}
          title={t`components.project-edit.uploading-files`}
        />
      )}
      <SubmitCancelButtons
        onCancel={onComplete}
        disableSubmit={isUploading}
        disableCancel={isUploading}
      />
    </Form>
  );
};
